import { useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateTime } from '@/lib/dateFormat';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ConfirmModal } from '@/components/ConfirmModal';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { formatLeadPhone } from '@/components/ui/PhoneInput';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { SelectField } from '@/components/ui/SelectField';

export function LeadTrashPage() {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState<{ id: string; type: 'restore' | 'delete'; name: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTrash = () => {
    setLoading(true);
    api.get('/leads/trash').then(({ data }) => setLeads(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchTrash(); }, []);

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      if (confirmAction.type === 'restore') {
        await api.post(`/leads/trash/${confirmAction.id}/restore`);
        toast('success', `"${confirmAction.name}" restored`);
      } else {
        await api.delete(`/leads/trash/${confirmAction.id}`);
        toast('success', `"${confirmAction.name}" permanently deleted`);
      }
      setConfirmAction(null);
      fetchTrash();
    } catch {
      toast('error', `Failed to ${confirmAction.type} lead`);
    } finally { setActionLoading(false); }
  };

  const formatDate = (d: string) => formatDateTime(d);

  const { sorted, sort, onSort } = useSort(leads);

  // ── Client-side pagination ──
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamp the page whenever the data set or page size shrinks (e.g. after a
  // delete/restore, or switching rows-per-page) so we never land on an empty page.
  useEffect(() => { setPage(p => Math.min(p, totalPages)); }, [totalPages]);
  const start = (page - 1) * pageSize;
  const pageRows = useMemo(() => sorted.slice(start, start + pageSize), [sorted, start, pageSize]);

  return (
    <DashboardLayout title="Trash">
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Trash</h1>
          <p className="text-xs text-slate-500 mt-0.5">{leads.length} deleted lead(s) — Restore or permanently delete</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-20 text-slate-400 text-sm">Trash is empty</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow className="border-b-2 border-slate-200">
                  <SortTh label="Sr. No" sortable={false} />
                  <SortTh label="Contact" sortKey="name" sort={sort} onSort={onSort} />
                  <SortTh label="Phone" sortable={false} />
                  <SortTh label="Email" sortable={false} />
                  <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                  <SortTh label="Assigned To" sortKey="assignedTo.name" sort={sort} onSort={onSort} />
                  <SortTh label="Deleted At" sortKey="deletedAt" sort={sort} onSort={onSort} />
                  <SortTh label="Actions" sortable={false} />
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {pageRows.map((lead, idx) => {
                  const phoneStr = formatLeadPhone(lead.phones?.[0]) || '—';
                  const emailStr = lead.emails?.[0]?.email || '—';
                  return (
                    <tr key={lead.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500 tabular-nums">{start + idx + 1}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-800">{lead.name}</p>
                        <p className="text-xs text-slate-400">{lead.leadNo ? `#${lead.leadNo}` : `#${lead.id.slice(-6).toUpperCase()}`}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{phoneStr}</td>
                      <td className="px-4 py-3 text-slate-600">{emailStr}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500">
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{lead.assignedTo?.name || '—'}</td>
                      <td className="px-4 py-3 text-xs text-red-400">{formatDate(lead.deletedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setConfirmAction({ id: lead.id, type: 'restore', name: lead.name })}
                            className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors"
                            title="Restore lead"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setConfirmAction({ id: lead.id, type: 'delete', name: lead.name })}
                            className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                            title="Permanently delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination footer */}
          {!loading && total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>Rows per page</span>
                <SelectField
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </SelectField>
                <span className="ml-1">
                  Showing {start + 1}–{Math.min(start + pageSize, total)} of {total}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500 mr-1">Page {page} of {totalPages}</span>
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.type === 'restore' ? 'Restore Lead?' : 'Permanently Delete?'}
        message={confirmAction?.type === 'restore'
          ? `Restore "${confirmAction?.name}" back to the leads list?`
          : `Permanently delete "${confirmAction?.name}"? This cannot be undone.`}
        confirmText={confirmAction?.type === 'restore' ? 'Restore' : 'Delete Forever'}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
        isLoading={actionLoading}
      />
    </DashboardLayout>
  );
}
